import React, { Component, ReactNode } from 'react';
import { Product } from '~/Api/User/ProductGroup/CaLabeling';
import { DebounceInput } from '~/Debounce';
import FormattedProductPrice from '~/FormattedProductPrice';
import parseHumanFloat from '~/utils/Number/parseHumanFloat';
import Price from '~/utils/Price';
import { PricedProduct } from '~/Product';
import ProductPriceInfo from '~/ProductPriceInfo';
import { Link } from '~/UI/Button';
import Checkbox from '~/UI/Checkbox/Checkbox';
import CountryIcon from '~/UI/CountryIcon/CountryIcon';
import inputCompactStyles from '~/UI/Input/compact.less';
import Input from '~/UI/Input/Input';

import styles from './styles.less';

export interface ReferencePriceValue {
	parentValue: null;
	prompt: ReactNode;
	value: string;
}
export interface Values {
	packageSize: {
		isValid: boolean | null;
		value: string;
	};
}
export type OnChangeListener = (product: Product, packageSizeForSingleUnit: string) => void;

interface Props {
	detailLink(product: { id: string }): { pathname: string; search: string };
	highlightProduct: boolean;
	pricedProduct: PricedProduct<Product>;
	onChange: OnChangeListener;
	values: Values;
	onSave(): void;
	priceType: string | null;
	isFetching: boolean;
	disablePackageSize: boolean;
	checked: boolean;
	checkRow(value: boolean): void;
}

export default class Row extends Component<Props> {
	shouldComponentUpdate(nextProps: Readonly<Props>) {
		return (
			this.props.highlightProduct !== nextProps.highlightProduct ||
			JSON.stringify(this.props.pricedProduct) !== JSON.stringify(nextProps.pricedProduct) ||
			this.props.onChange !== nextProps.onChange ||
			this.props.values.packageSize.isValid !== nextProps.values.packageSize.isValid ||
			this.props.values.packageSize.value !== nextProps.values.packageSize.value ||
			this.props.priceType !== nextProps.priceType ||
			this.props.isFetching !== nextProps.isFetching ||
			this.props.disablePackageSize !== nextProps.disablePackageSize ||
			this.props.checked !== nextProps.checked
		);
	}

	public render() {
		const {
			pricedProduct,
			values,
			highlightProduct,
			detailLink,
			isFetching,
			disablePackageSize,
			onChange,
			onSave,
			checked,
			checkRow,
		} = this.props;
		const { product } = pricedProduct;
		const detailLinkString = detailLink(product);

		return (
			<div
				className={
					product.status.isRemoved() || product.status.isBeforeIntroduction()
						? styles.removedRow
						: highlightProduct
						? styles.highlightedRow
						: styles.row
				}
				data-testid='row'
			>
				<div className={styles.checkboxContainer} data-testid='delete-product'>
					<Checkbox checked={checked} onChange={checkRow} />
				</div>
				<div className={styles.countryCell}>
					<Link styles={{ button: styles.countryCellContent }} tabIndex={-1} to={detailLinkString}>
						<div className={styles.countryIcon}>
							<CountryIcon code={product.dataset.source.country.alpha2} name={product.dataset.source.country.name} />
						</div>
						{product.dataset.source.country.alpha2}
						<abbr
							className={styles.datasetSourceShortName}
							title={`${product.dataset.source.shortName} — ${product.dataset.source.description}`}
						>
							{product.dataset.source.shortName}
						</abbr>
					</Link>
				</div>
				<div className={styles.brandCell}>
					<Link styles={{ button: styles.brandCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedBrandName}
					</Link>
				</div>
				<div className={styles.mahCell}>
					<Link styles={{ button: styles.mahCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedMarketingAuthorizationHolder}
					</Link>
				</div>
				<div className={styles.dosageCell}>
					<Link styles={{ button: styles.dosageCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedDosageForm}
					</Link>
				</div>
				<div className={styles.dosageStrengthCell}>
					<Link styles={{ button: styles.dosageStrengthCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.dosageStrength}
					</Link>
				</div>
				<div className={styles.packageCell}>
					<Link styles={{ button: styles.packageCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.packageSize}
					</Link>
				</div>
				<div className={styles.priceCell}>
					<Link styles={{ button: styles.priceCellContent }} tabIndex={-1} to={detailLinkString}>
						<FormattedProductPrice
							rounded
							dataSource={product.dataset.source}
							isFetching={isFetching}
							productPrice={pricedProduct.price}
						/>
					</Link>
				</div>
				<div className={styles.priceTypeCell}>
					<Link styles={{ button: styles.priceTypeCellContent }} tabIndex={-1} to={detailLinkString}>
						{this.renderPriceType()}
					</Link>
				</div>
				<div className={styles.packageForCalculationCell}>
					{disablePackageSize ? (
						<div className={styles.packageCellContent}>1</div>
					) : (
						<div className={styles.packageInput}>
							<DebounceInput
								value={values.packageSize.value}
								onBlur={onSave}
								onChange={(value) => {
									onChange(pricedProduct.item, value);
								}}
							>
								<Input
									id={`product-package-for-calc-${product.id}`}
									isInvalid={values.packageSize.isValid === false}
									placeholder='1'
									styles={inputCompactStyles}
								/>
							</DebounceInput>
						</div>
					)}
				</div>
				<div className={styles.priceCell}>
					<Link styles={{ button: styles.priceCellContent }} tabIndex={-1} to={detailLinkString}>
						{this.renderCalculatedPrice()}
					</Link>
				</div>
			</div>
		);
	}

	private renderPriceType() {
		const {
			priceType,
			pricedProduct: { product, price },
		} = this.props;

		return (
			<>
				{price !== null ? <span title={price.humanFormatType()}>{price.humanCondensedFormatType()}</span> : null}
				<ProductPriceInfo price={price} priceType={priceType} product={product} />
			</>
		);
	}

	private renderCalculatedPrice() {
		const {
			pricedProduct,
			values: { packageSize },
		} = this.props;
		if (packageSize.isValid === false || (packageSize.isValid === null && packageSize.value !== '')) {
			return null;
		}

		const { price } = pricedProduct;
		if (price === null || price.price === null) {
			return null;
		}

		const parsedPackageSize = packageSize.value !== '' ? parseHumanFloat(packageSize.value) : 1;
		const value = Number(price.price.value) / (parsedPackageSize ?? 1);
		const unitPrice = new Price(String(value), price.price.currency);
		return unitPrice.roundedHumanFormat();
	}
}
